import { useCallback, useRef, useState } from "react";
import { postAuthConnectRequests } from "~/_api/auth/postAuthConnectRequests";
import { postAuthConnectRequestsRegister } from "~/_api/auth/postAuthConnectRequestsRegister";
import { EAccount } from "~/_enums/EAccount";
import { ECode } from "~/_enums/ECode";
import { useTranslation } from "~/hooks/useTranslation";
import { ENameSpace } from "~/i18n/@types/types";
import { useAlert } from "~/modules/Common/Overlay";

import { EPathname } from "~/_enums/EPathname";
import { buildQuery } from "~/_utils/calibrator/urlCalibrator";
import { assert, assertIsDefined } from "~/_utils/assert";
import { postCommonCreateShortAuthToken } from "~/_api/auth/postCommonCreateShortAuthToken";
import { postAuthConnectRequestsConsume } from "~/_api/auth/postAuthConnectRequestsConsume";
import {
  ERROR_AUTH_ALREADY_USED_ACCOUNT,
  ERROR_AUTH_CONNECT_CONSUME_BAD_REQUEST,
  ERROR_AUTH_CONNECT_CONSUME_MAXIMUM,
} from "~/_constants/errors";
import { useElectronOpenUrl } from "~/router/hooks/useElectronOpenUrl";
import { EDesktopAppDeeplink } from "~/_enums/EDesktopAppDeeplink";
import { TAuthConnectRequestsConsumeSuccessData } from "~/_types/TAuthRequestsConsumeSuccessData";
import { env } from "~/env";

export const useGoogleConnect = (
  onSuccess: (consumeData: TAuthConnectRequestsConsumeSuccessData) => void,
) => {
  const { openAlert } = useAlert();
  const { t } = useTranslation([ENameSpace.Common]);
  const [isConsuming, setIsConsuming] = useState(false);
  const requestResponseDataRef = useRef<{
    requestId: string;
    requestKey: string;
  } | null>(null);
  const asLoginAccountRef = useRef(false);

  const handleConsume = useCallback(
    async (requestId: string, requestKey: string) => {
      try {
        setIsConsuming(true);

        // 4. 인증 요청 사용
        const consumeResponse = await postAuthConnectRequestsConsume(
          {
            authRequestId: requestId,
            key: requestKey,
          },
          {},
        );
        if (consumeResponse.code !== ECode.Success) {
          if (consumeResponse.code === "010") {
            throw new Error(ERROR_AUTH_CONNECT_CONSUME_MAXIMUM);
          } else if (consumeResponse.code === "003") {
            throw new Error(ERROR_AUTH_CONNECT_CONSUME_BAD_REQUEST);
          } else if (consumeResponse.code === "101") {
            throw new Error(ERROR_AUTH_ALREADY_USED_ACCOUNT);
          }

          throw new Error();
        }

        onSuccess(consumeResponse.data);
      } catch (error) {
        if (
          error instanceof Error &&
          error.message === ERROR_AUTH_CONNECT_CONSUME_MAXIMUM
        ) {
          openAlert({
            text: t(
              `${ENameSpace.Common}:OAuth.ErrorMessage.TheValidityPeriodHasExpired`,
            ),
          });
        } else if (
          error instanceof Error &&
          error.message === ERROR_AUTH_CONNECT_CONSUME_BAD_REQUEST
        ) {
          openAlert({
            text: t(
              `${ENameSpace.Common}:OAuth.ErrorMessage.ThisIsAnInvalidRequest`,
            ),
          });
        } else if (
          error instanceof Error &&
          error.message === ERROR_AUTH_ALREADY_USED_ACCOUNT
        ) {
          openAlert({
            text: t(
              `${ENameSpace.Common}:OAuth.ErrorMessage.ThisYouTubeChannelIsAlreadyLinked`,
            ),
          });
        } else {
          openAlert({
            text: t(
              `${ENameSpace.Common}:OAuth.ErrorMessage.AccountLinkingFailed`,
            ),
          });
        }
      } finally {
        setIsConsuming(false);
      }
    },
    [onSuccess, openAlert, t],
  );

  useElectronOpenUrl((data) => {
    if (data.url === EDesktopAppDeeplink.AuthConnectConsume) {
      assertIsDefined(requestResponseDataRef.current);
      handleConsume(
        requestResponseDataRef.current.requestId,
        requestResponseDataRef.current.requestKey,
      );
    }
  });

  const handleGoogleConnect = useCallback(
    async (params?: { asLoginAccount: boolean }) => {
      asLoginAccountRef.current = params?.asLoginAccount ?? false;
      try {
        // 1. 인증 요청 생성
        const requestResponse = await postAuthConnectRequests({});
        if (requestResponse.code !== ECode.Success) {
          throw new Error();
        }
        requestResponseDataRef.current = {
          requestId: requestResponse.data.id,
          requestKey: requestResponse.data.key,
        };

        // 2. 인증 요청 데이터 등록
        const registerResponse = await postAuthConnectRequestsRegister(
          {
            authRequestId: requestResponse.data.id,
            key: requestResponse.data.key,
            payload: {
              account: EAccount.Google,
              oauthRedirectUrl:
                window.location.origin + EPathname.OAuthGoogleConnectCallback,
              asLoginAccount: asLoginAccountRef.current,
            },
          },
          {},
        );
        if (registerResponse.code !== ECode.Success) {
          throw new Error();
        }

        const shortAuthTokenResponse = await postCommonCreateShortAuthToken({});
        if (shortAuthTokenResponse.code !== ECode.Success) {
          throw new Error();
        }

        assert(registerResponse.data.account === EAccount.Google);
        const url = `${EPathname.OAuthGoogleConnectSetup}?${buildQuery({
          requestId: requestResponse.data.id,
          requestKey: requestResponse.data.key,
          authorizationUrl: registerResponse.data.authorizationUrl,
          fromDesktopApp: env.IS_DESKTOP_APP ? 1 : undefined,
          authToken: shortAuthTokenResponse.data.token,
          asLogin: asLoginAccountRef.current,
        })}`;

        window.onAuthVerifySuccess = () =>
          handleConsume(requestResponse.data.id, requestResponse.data.key);
        window.open(url);
      } catch (error) {
        openAlert({
          text: t(
            `${ENameSpace.Common}:OAuth.ErrorMessage.AccountLinkingFailed`,
          ),
        });
      }
    },
    [handleConsume, openAlert, t],
  );

  return {
    handleGoogleConnect,
    isConsuming,
  };
};
